import { useEffect, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useUI } from "@/context/UIContext";
import { vehicleService, geofenceService } from "@/services/api";
import { onRealtimeEvent } from "@/lib/socket";
import { MapCanvas, MapLegend } from "@/components/common/MapCanvas";
import { VehiclePanel } from "@/components/common/VehiclePanel";
import { StatusBadge, STATUS } from "@/components/common/StatusBadge";
import { SkeletonMap } from "@/components/common/Skeletons";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search } from "lucide-react";

const FILTERS = ["all", "moving", "parked", "idle", "offline", "alert"];
// Fallback only now — the socket effect below refetches instantly on a real
// push. This just guards against a dropped/reconnecting Socket.IO session.
const REFRESH_MS = 15000;
// A burst of position events (several vehicles, or one sending rapidly)
// should trigger one refetch, not one per event.
const REALTIME_DEBOUNCE_MS = 400;

export default function LiveMap() {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const { facilities } = useUI();
  const { data: vehicles, loading, refetch } = useFetch(() => vehicleService.list(), []);
  const { data: geofences } = useFetch(() => geofenceService.list(), []);

  // "Live" map — keep positions fresh without the user having to reload.
  useEffect(() => {
    const id = setInterval(refetch, REFRESH_MS);
    return () => clearInterval(id);
  }, [refetch]);

  // Primary path: the backend pushes one of these the moment a webhook is
  // processed (see backend/app/services/realtime.py). The vehicle list
  // itself, not just the changed field, is re-fetched — that reuses the
  // same mapping vehicleService.list() already does rather than duplicating
  // the backend's field shape here, at the cost of a full re-fetch per
  // burst instead of an in-place patch.
  useEffect(() => {
    let debounceId = null;
    const scheduleRefetch = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(refetch, REALTIME_DEBOUNCE_MS);
    };
    const unsubscribers = [
      onRealtimeEvent("vehicle.position", scheduleRefetch),
      onRealtimeEvent("vehicle.status", scheduleRefetch),
      onRealtimeEvent("trip.started", scheduleRefetch),
      onRealtimeEvent("trip.ended", scheduleRefetch),
    ];
    return () => {
      clearTimeout(debounceId);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [refetch]);

  let list = vehicles || [];
  if (filter !== "all") list = list.filter((v) => v.status === filter);
  if (q) list = list.filter((v) => (v.name + v.plate + v.location.address).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="relative h-[calc(100vh-4rem)]">
      {loading && !vehicles ? <SkeletonMap className="h-full !rounded-none" /> : (
        <MapCanvas
          testId="live-map"
          viewportKey="live-map"
          vehicles={list}
          geofences={geofences || []}
          facilities={facilities}
          selectedId={selected?.id}
          onSelect={setSelected}
          className="!rounded-none !border-0 h-full"
        />
      )}

      {/* Floating vehicle list — left */}
      <div className="absolute left-4 top-4 flex w-[300px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border/70 glass-strong">
        <div className="border-b border-border/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-display text-sm font-semibold">Live Fleet</h2>
            <span className="text-xs text-muted-foreground">{list.length} vehicles</span>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vehicles…" className="h-9 pl-8 text-sm" data-testid="livemap-search" />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-medium capitalize transition",
                  filter === f ? "border-primary/40 bg-primary/15 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"
                )}
                data-testid={`livemap-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[calc(100vh-16rem)] space-y-1.5 overflow-y-auto p-2 no-scrollbar">
          {list.map((v) => {
            const S = STATUS[v.status];
            return (
              <button
                key={v.id}
                onClick={() => setSelected(v)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition",
                  selected?.id === v.id ? "border-primary/40 bg-primary/10" : "border-transparent hover:border-border/60 hover:bg-secondary/40"
                )}
              >
                <span className={cn("h-2 w-2 shrink-0 rounded-full", S.dot)} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{v.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{v.location.address}</div>
                </div>
                <span className="font-telemetry text-xs text-muted-foreground">{v.status === "moving" ? `${v.speed}` : ""}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contextual panel — right */}
      {selected && (
        <div className="absolute right-4 top-4 bottom-4 w-[340px] max-w-[calc(100vw-2rem)]">
          <VehiclePanel vehicle={selected} onClose={() => setSelected(null)} className="h-full" />
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
        <MapLegend />
      </div>
    </div>
  );
}
